/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-console */
import {
  CalendarOutlined,
  DownOutlined,
  HomeOutlined,
  SearchOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import elGR from "antd/es/date-picker/locale/el_GR";
import React from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import moment, { Moment } from "moment";
import { ICity, ISelectOption } from "../../../common/typings";
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Select,
  Input,
} from "../../atoms";
import { HotelSearchBoxWrapper } from "./HotelSearchBox.style";
interface IProps {
  cities: ICity[];
  callBack?: () => void;
  adultNumbers?: number;
  childrenNumber?: number;
  infant?: number;
  city?: string;
  startDate?: string | null;
  endDate?: string | null;
  period?: string;
  searchButtonLabel?: string;
  hideSelectedCity?: boolean;
  hideCityField?: boolean;
}

export const HotelSearchBox: React.FC<IProps> = ({
  cities,
  callBack,
  adultNumbers = 2,
  childrenNumber = 0,
  infant = 0,
  city = "all",
  startDate,
  endDate,
  searchButtonLabel,
  hideSelectedCity = false,
  hideCityField = false,
}) => {
  const history = useNavigate();
  const { formatMessage } = useIntl();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ query: "(max-width: 600px)" });
  const [loading, setLoading] = React.useState(false);
  const [dateError, setDateError] = React.useState("");
  const [searchCities, setSearchCities] = React.useState<ISelectOption[]>([]);
  const [adultsCount, setAdultsCount] = React.useState(adultNumbers);
  const [childrenCount, setChildrenCount] = React.useState(childrenNumber);
  const roomsCount = 1;
  const [infantCount, setInfantCount] = React.useState(infant);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [form] = Form.useForm();
  const initialCityValue = city || "all";
  const initialCheckIn = startDate ? moment(startDate, "YYYY-MM-DD") : null;
  const initialCheckOut = endDate ? moment(endDate, "YYYY-MM-DD") : null;
  const [dateRange, setDateRange] = React.useState<
    [Moment | null, Moment | null]
  >(() => [initialCheckIn, initialCheckOut]);
  const maxStayMessage =
    "Δεν επιτρέπονται διαμονές άνω των 15 διανυκτερεύσεων! Παρακαλώ αλλάξετε τις ημερομηνίες ώστε να προχωρήσετε με την Αναζήτηση σας. ";

  const updateStayError = (start: Moment | null, end: Moment | null) => {
    if (!start || !end) {
      setDateError("");
      return;
    }
    const nights = end.diff(start, "days");
    setDateError(nights > 15 ? maxStayMessage : "");
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values: any = await form.validateFields();
      const selectedCity =
        values.city || (hideCityField ? city : undefined) || "all";
      const searchStartDate = dateRange?.[0]?.format("YYYY-MM-DD") || "";
      const searchEndDate = dateRange?.[1]?.format("YYYY-MM-DD") || "";
      if (dateRange?.[0] && dateRange?.[1]) {
        const nights = dateRange[1].diff(dateRange[0], "days");
        if (nights > 15) {
          setDateError(maxStayMessage);
          setLoading(false);
          return;
        }
      }
      const searchParams = new URLSearchParams({
        city: `${selectedCity}`,
        adultNumbers: `${adultsCount}`,
        childrenNumber: `${childrenCount}`,
        infant: `${infantCount}`,
        rooms: `${roomsCount}`,
      });
      if (searchStartDate && searchEndDate) {
        searchParams.append("startDate", searchStartDate);
        searchParams.append("endDate", searchEndDate);
      }
      history(`/hotels?${searchParams.toString()}`);
      callBack && callBack();
      setLoading(false);
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const defaultCities = [...cities]
      .sort((a, b) => a?.name?.localeCompare(b?.name))
      .map((selectedCity) => ({
        value: selectedCity.code,
        label: selectedCity.name,
      }));
    setSearchCities([
      {
        value: "all",
        label: formatMessage({
          defaultMessage: "Any Where",
          id: "d0ml+9",
        }),
      },
      ...defaultCities,
    ]);
  }, [cities, formatMessage]);

  React.useEffect(() => {
    form.setFieldsValue({
      adultNumbers: adultsCount,
      childrenNumber: childrenCount,
      infant: infantCount,
      rooms: roomsCount,
    });
  }, [adultsCount, childrenCount, infantCount, roomsCount, form]);

  React.useEffect(() => {
    if (startDate && endDate) {
      const start = moment(startDate, "YYYY-MM-DD");
      const end = moment(endDate, "YYYY-MM-DD");
      setDateRange([start, end]);
      form.setFieldsValue({ checkin: start, checkout: end });
    }
  }, [startDate, endDate, form]);

  const occupancySummary = `${adultsCount} Ενήλικες · ${childrenCount} Παιδιά · ${infantCount} Βρέφη`;

  const handleCountChange = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    value: number,
    min: number,
    max?: number,
  ) => {
    let nextValue = value;
    if (nextValue < min) nextValue = min;
    if (max && nextValue > max) nextValue = max;
    setter(nextValue);
    form.setFieldsValue({
      adultNumbers:
        setter === setAdultsCount
          ? nextValue
          : form.getFieldValue("adultNumbers"),
      childrenNumber:
        setter === setChildrenCount
          ? nextValue
          : form.getFieldValue("childrenNumber"),
      infant:
        setter === setInfantCount ? nextValue : form.getFieldValue("infant"),
    });
  };

  const handleCheckInChange = (value: Moment | null) => {
    let nextEnd = dateRange[1];
    if (value) {
      if (!nextEnd || nextEnd.isBefore(value, "day")) {
        nextEnd = value.clone().add(1, "day");
      }
    }
    setDateRange([value, nextEnd]);
    form.setFieldsValue({ checkin: value, checkout: nextEnd });
    form.validateFields(["checkout"]);
    updateStayError(value, nextEnd);
  };

  const handleCheckOutChange = (value: Moment | null) => {
    let nextStart = dateRange[0];
    if (value && nextStart && value.isBefore(nextStart, "day")) {
      nextStart = value;
    }
    setDateRange([nextStart, value]);
    form.setFieldsValue({ checkin: nextStart, checkout: value });
    form.validateFields(["checkout"]);
    updateStayError(nextStart, value);
  };

  const occupancyOverlay = (
    <div className="occupancy-panel">
      <div className="occ-row">
        <span className="occ-label">Ενήλικες (12+)</span>
        <div className="counter">
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setAdultsCount, adultsCount - 1, 1, 8)
            }
          >
            -
          </button>
          <span className="counter-value">{adultsCount}</span>
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setAdultsCount, adultsCount + 1, 1, 8)
            }
          >
            +
          </button>
        </div>
      </div>
      <div className="occ-row">
        <span className="occ-label">Παιδιά (2-11 Ετών)</span>
        <div className="counter">
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setChildrenCount, childrenCount - 1, 0, 6)
            }
          >
            -
          </button>
          <span className="counter-value">{childrenCount}</span>
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setChildrenCount, childrenCount + 1, 0, 6)
            }
          >
            +
          </button>
        </div>
      </div>
      <div className="occ-row">
        <span className="occ-label">Βρέφη (0-23 Μηνών)</span>
        <div className="counter">
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setInfantCount, infantCount - 1, 0, 1)
            }
          >
            -
          </button>
          <span className="counter-value">{infantCount}</span>
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setInfantCount, infantCount + 1, 0, 1)
            }
          >
            +
          </button>
        </div>
      </div>
      <div className="occupancy-info">
        *Οι ηλικίες των παιδιών και ο αριθμός ατόμων που δηλώνετε στην κράτηση
        σας, θα τηρούνται αυστηρά, διαφορετικά ενδέχεται να υπάρχει επιπλέον
        χρέωση κατά την άφιξη σας στο ξενοδοχείο ή ακόμη και να μην σας
        παραχωρηθεί το δωμάτιο. Το γραφείο Poupas for Holidays δεν θα φέρει
        καμία ευθύνη.
      </div>

      <Button className="done-btn" onClick={() => setDropdownOpen(false)} block>
        Τέλος
      </Button>
    </div>
  );

  return (
    <HotelSearchBoxWrapper
      ref={wrapperRef}
      className={hideSelectedCity ? "hide-selected-city" : undefined}
    >
      <Form
        form={form}
        name="horizontal_login"
        layout="vertical"
        className="hotel-search-form"
        initialValues={{
          adultNumbers,
          childrenNumber,
          infant: infantCount,
          city: initialCityValue,
          rooms: roomsCount,
          checkin: initialCheckIn,
          checkout: initialCheckOut,
        }}
      >
        <div className="search-row">
          {!hideCityField && (
            <div className="input-shell">
              <span className="field-icon-lg">
                <HomeOutlined />
              </span>
              <Form.Item name="city" className="no-label" style={{ flex: 1 }}>
                <Select
                  options={searchCities}
                  placeholder="Που πάτε;"
                  optionFilterProp="label"
                  filterOption={(input, option: any) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  dropdownMatchSelectWidth={260}
                  dropdownClassName="hotel-city-dropdown"
                  getPopupContainer={() => wrapperRef.current || document.body}
                />
              </Form.Item>
            </div>
          )}
          <div className="input-shell date-shell">
            {!isMobile && (
              <span className="field-icon-lg">
                <CalendarOutlined />
              </span>
            )}
            <div className="date-grid">
              <div className="date-field">
                <Form.Item
                  name="checkin"
                  className="no-label"
                  rules={[
                    {
                      required: true,
                      message: "Επιλέξτε άφιξη",
                    },
                  ]}
                >
                  <DatePicker
                    value={dateRange[0] as any}
                    onChange={(value) => handleCheckInChange(value)}
                    format="D MMM YYYY"
                    locale={elGR}
                    allowClear={false}
                    getPopupContainer={() =>
                      wrapperRef.current || document.body
                    }
                    disabledDate={(current) =>
                      !!current && current < moment().startOf("day")
                    }
                    popupClassName="hotel-date-popup"
                    placement="bottomLeft"
                    inputReadOnly={isMobile}
                  />
                </Form.Item>
              </div>
              <div className="date-divider" />
              <div className="date-field">
                <Form.Item
                  name="checkout"
                  className="no-label"
                  rules={[
                    {
                      required: true,
                      message: "Επιλέξτε αναχώρηση",
                    },
                  ]}
                >
                  <DatePicker
                    value={dateRange[1] as any}
                    onChange={(value) => handleCheckOutChange(value)}
                    format="D MMM YYYY"
                    locale={elGR}
                    allowClear={false}
                    getPopupContainer={() =>
                      wrapperRef.current || document.body
                    }
                    disabledDate={(current) => {
                      if (!current) return false;
                      const today = moment().startOf("day");
                      if (current < today) return true;
                      if (dateRange[0]) {
                        return current.isBefore(dateRange[0], "day");
                      }
                      return false;
                    }}
                    popupClassName="hotel-date-popup"
                    placement="bottomLeft"
                    inputReadOnly={isMobile}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
          <Dropdown
            overlay={occupancyOverlay}
            trigger={["click"]}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            getPopupContainer={() => wrapperRef.current || document.body}
            placement="bottomLeft"
          >
            <div className="input-shell occupancy-shell">
              <span className="field-icon-lg">
                <UserAddOutlined />
              </span>
              <div className="occupancy-label">{occupancySummary}</div>
              <DownOutlined className="dropdown-icon" />
            </div>
          </Dropdown>
          <Button
            loading={loading}
            onClick={handleSubmit}
            className="search-button"
          >
            <SearchOutlined />{" "}
            {searchButtonLabel ? (
              searchButtonLabel
            ) : (
              <FormattedMessage
                description="Search"
                defaultMessage="Search"
                id="2NXYYu"
              />
            )}
          </Button>
        </div>
        {dateError && <Alert message={dateError} type="error" />}
        <Form.Item name="adultNumbers" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="childrenNumber" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="infant" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="rooms" hidden>
          <Input type="hidden" />
        </Form.Item>
      </Form>
    </HotelSearchBoxWrapper>
  );
};
